<template>
    <BasicModal
        :title="`Days off & budget for ${year}`"
        @close="$emit('close')"
    >
        <form novalidate @submit.prevent="onSave">
            <div class="form-field">
                <label>
                    <input v-model="daysOffEnabled" type="checkbox" />
                    I have a limited number of days off
                </label>
                <div
                    v-if="daysOffEnabled"
                    class="input-affixed input-affixed--suffix"
                >
                    <input
                        v-model.number="days"
                        type="number"
                        class="input-no-border"
                        min="0"
                        max="366"
                        aria-label="Days off per year"
                    />
                    <span class="input-affixed__suffix">days</span>
                </div>
                <small v-if="daysIsSuggested">
                    Suggested, carried over from last year
                </small>
                <FormError :errors="errors.vacation_days_allowance" />
            </div>
            <div class="form-field">
                <label>
                    <input v-model="budgetEnabled" type="checkbox" />
                    I'm tracking a travel budget
                </label>
                <div
                    v-if="budgetEnabled"
                    class="input-affixed input-affixed--prefix"
                >
                    <span class="input-affixed__prefix">{{ currency }}</span>
                    <input
                        v-model.number="budget"
                        type="number"
                        class="input-no-border"
                        min="0"
                        step="0.01"
                        :aria-label="`Travel budget per year, in ${currency}`"
                    />
                </div>
                <FormError :errors="errors.money_allowance" />
            </div>
            <div class="actions">
                <Bttn type="submit" :disabled="saving">
                    {{ saving ? "Saving…" : "Save" }}
                </Bttn>
            </div>
        </form>
    </BasicModal>
</template>

<script>
import BasicModal from "js/components/modals/BasicModal";
import FormError from "js/components/forms/FormError";
import { edit, handleResponse } from "js/lib/server";

function asFiniteOrNull(value) {
    if (value === null || value === undefined) {
        return null;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

export default {
    name: "YearSettingsModal",
    components: { BasicModal, FormError },
    props: {
        year: {
            type: Number,
            required: true,
        },
        vacationDays: {
            type: [Number, String],
            default: null,
        },
        money: {
            type: [Number, String],
            default: null,
        },
        suggestedVacationDays: {
            type: [Number, String],
            default: null,
        },
        currency: {
            type: String,
            default: "$",
        },
    },
    emits: ["close", "saved"],
    data() {
        const currentDays = asFiniteOrNull(this.vacationDays);
        const suggestedDays = asFiniteOrNull(this.suggestedVacationDays);
        const currentMoney = asFiniteOrNull(this.money);
        return {
            daysOffEnabled: currentDays !== null,
            days: currentDays ?? suggestedDays ?? 0,
            daysIsSuggested: currentDays === null && suggestedDays !== null,
            budgetEnabled: currentMoney !== null,
            budget: currentMoney ?? 0,
            errors: {},
            saving: false,
        };
    },
    watch: {
        days() {
            this.daysIsSuggested = false;
        },
    },
    methods: {
        async onSave() {
            const vacationDaysAllowance = this.daysOffEnabled
                ? Math.round(Number(this.days))
                : null;
            const moneyAllowance = this.budgetEnabled
                ? Number(this.budget)
                : null;

            this.errors = {};
            if (
                this.daysOffEnabled &&
                (!Number.isInteger(vacationDaysAllowance) ||
                    vacationDaysAllowance < 0 ||
                    vacationDaysAllowance > 366)
            ) {
                this.errors.vacation_days_allowance = [
                    "Enter a whole number of days between 0 and 366.",
                ];
            }
            if (
                this.budgetEnabled &&
                (!Number.isFinite(moneyAllowance) || moneyAllowance < 0)
            ) {
                this.errors.money_allowance = [
                    "Enter a valid, non-negative budget.",
                ];
            }
            if (Object.keys(this.errors).length) {
                return;
            }

            this.saving = true;
            try {
                const setting = await edit(
                    `/api/travel-schedule/${this.year}/settings`,
                    {
                        vacation_days_allowance: vacationDaysAllowance,
                        money_allowance: moneyAllowance,
                    }
                ).then(handleResponse);
                this.$emit("saved", setting);
                this.$emit("close");
            } catch (err) {
                this.errors = err.response?.data?.errors ?? {
                    vacation_days_allowance: [
                        "Could not save your days off and budget. Please try again.",
                    ],
                };
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>
